import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, type Me } from '../api';
import { errorText, useI18n } from '../i18n';
import { Alert, AuthCard } from '../layout';
import { oauthMessage, SignInWith } from './sign-in-with';

type Role = 'owner' | 'member' | 'viewer';
interface Member { userId: number; name: string; email: string; role: Role; joinedAt: string; lastLoginAt: string | null; viaFirm: string | null }
interface Invite { id: number; email: string; role: 'member' | 'viewer'; createdAt: string; expiresAt: string }

export const ROLE_LABELS: Record<Role, string> = { owner: 'Savininkas', member: 'Narys', viewer: 'Tik peržiūra' };
const ROLE_HINTS: Record<Role, string> = {
  owner: 'Viskas, taip pat planas, mokėjimai, priedai ir komanda.',
  member: 'Sąskaitos, dokumentai, klientai ir kita kasdienė veikla.',
  viewer: 'Tik mato duomenis, nieko nekeičia (pvz., išorinis buhalteris).',
};

/** Nustatymai → Komanda: people of the company, invitations, roles. */
export function TeamCard({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const team = useQuery({ queryKey: ['team-members'], queryFn: () => api.get<{ members: Member[]; invites: Invite[] }>('api/v1/team/members') });
  const act = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team-members'] }) });
  const [sent, setSent] = useState<{ email: string; mailSent: boolean } | null>(null);
  const owner = me.company.role === 'owner' && !me.viewAsAdmin;
  const invite = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const d = new FormData(form);
    const email = String(d.get('email'));
    act.mutate(async () => {
      const r = await api.post<{ mailSent: boolean }>('api/v1/team/invites', { email, role: d.get('role') });
      setSent({ email, mailSent: r.mailSent });
      form.reset();
    });
  };
  const data = team.data;
  return (
    <>
      <section className="panel-card">
        <p className="eyebrow">{t('Komanda')}</p><h2>{t('Žmonės įmonėje')}</h2>
        {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
        <ul className="team-list">
          {data?.members.map((m) => (
            <li key={m.userId}>
              <div className="customer-avatar">{m.name.slice(0, 2).toUpperCase()}</div>
              <div className="team-who"><strong>{m.name}{m.userId === me.user.id ? ` (${t('jūs')})` : ''}</strong><small>{m.email}{m.lastLoginAt ? ` · ${t('paskutinį kartą {date}', { date: m.lastLoginAt.slice(0, 10) })}` : ''}</small></div>
              {m.viaFirm ? <span className="badge" title={t('Buhalterinės įmonės žmogus: išeina nutraukus ryšį su įmone.')}>{t('per {firm}', { firm: m.viaFirm })}</span> : owner && m.userId !== me.user.id ? (
                <>
                  <select aria-label={t('Teisės')} value={m.role} disabled={act.isPending} onChange={(e) => act.mutate(() => api.put(`api/v1/team/members/${m.userId}`, { role: e.target.value }))}>
                    {(Object.keys(ROLE_LABELS) as Role[]).map((r) => <option key={r} value={r}>{t(ROLE_LABELS[r])}</option>)}
                  </select>
                  <button type="button" className="delete-x" aria-label={t('Pašalinti iš įmonės?')} disabled={act.isPending}
                    onClick={() => { if (confirm(t('Pašalinti {name} iš įmonės? Prie šios įmonės nebebus galima prisijungti.', { name: m.name }))) act.mutate(() => api.delete(`api/v1/team/members/${m.userId}`)); }}>×</button>
                </>
              ) : <span className="badge">{t(ROLE_LABELS[m.role])}</span>}
            </li>
          ))}
        </ul>
        {data?.invites.length ? <>
          <p className="eyebrow team-sub">{t('Laukiantys kvietimai')}</p>
          <ul className="team-list">
            {data.invites.map((i) => (
              <li key={i.id}>
                <div className="customer-avatar">✉</div>
                <div className="team-who"><strong>{i.email}</strong><small>{t(ROLE_LABELS[i.role])} · {t('galioja iki {date}', { date: i.expiresAt.slice(0, 10) })}</small></div>
                {owner ? <button type="button" className="button button--ghost button--small" disabled={act.isPending} onClick={() => act.mutate(() => api.delete(`api/v1/team/invites/${i.id}`))}>{t('Atšaukti')}</button> : null}
              </li>
            ))}
          </ul>
        </> : null}
        {!owner && !me.viewAsAdmin ? (
          <div className="form-actions team-leave">
            <button type="button" className="button button--danger button--small" disabled={act.isPending}
              onClick={() => { if (confirm(t('Palikti įmonę „{company}“? Grįžti galėsite tik su nauju kvietimu.', { company: me.company.name }))) act.mutate(async () => { await api.post('api/v1/team/leave'); window.location.assign(document.baseURI); }); }}>{t('Palikti įmonę')}</button>
          </div>
        ) : null}
      </section>
      {owner ? (
        <section className="panel-card">
          <p className="eyebrow">{t('Kvietimas')}</p><h2>{t('Pakviesti žmogų')}</h2>
          {sent ? <Alert kind={sent.mailSent ? 'success' : 'error'}>{sent.mailSent ? t('Kvietimas išsiųstas: {email}. Nuoroda galioja 7 d.', { email: sent.email }) : t('Kvietimas sukurtas, bet laiško išsiųsti nepavyko. Bandykite dar kartą vėliau.')}</Alert> : null}
          <form className="team-invite" onSubmit={invite}>
            <label>{t('El. paštas')}<input type="email" name="email" required maxLength={200} /></label>
            <label>{t('Teisės')}<select name="role" defaultValue="member">
              <option value="member">{t(ROLE_LABELS.member)}</option><option value="viewer">{t(ROLE_LABELS.viewer)}</option><option value="owner">{t(ROLE_LABELS.owner)}</option>
            </select></label>
            <div className="form-actions"><button className="button button--primary" disabled={act.isPending}>{t('Siųsti kvietimą')}</button></div>
          </form>
          <ul className="team-roles">
            {(Object.keys(ROLE_LABELS) as Role[]).map((r) => <li key={r}><strong>{t(ROLE_LABELS[r])}</strong> — {t(ROLE_HINTS[r])}</li>)}
          </ul>
        </section>
      ) : null}
    </>
  );
}

interface InviteInfo { company: string; email: string; role: 'member' | 'viewer'; inviter: string | null; state: 'open' | 'used' | 'revoked' | 'expired'; account: boolean }
const STATE_TEXT: Record<Exclude<InviteInfo['state'], 'open'>, string> = {
  used: 'Šis kvietimas jau priimtas. Prisijunkite.', revoked: 'Kvietimas atšauktas.', expired: 'Kvietimo galiojimas baigėsi. Paprašykite naujo.',
};

/** The page the invitation e-mail opens: a new person sets a name and password; an existing one confirms with theirs. */
export function InvitePage({ token, oauth }: { token: string; oauth?: string }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lookup = useQuery({ queryKey: ['invite', token], enabled: Boolean(token), retry: false,
    queryFn: () => api.post<{ invite: InviteInfo }>('api/v1/invites/lookup', { token }).then((r) => r.invite) });
  const [error, setError] = useState<string | null>(null);
  const accept = useMutation({
    mutationFn: (body: object) => api.post('api/v1/invites/accept', { token, locale, ...body }),
    onSuccess: async () => { queryClient.clear(); await navigate({ to: '/' }); },
  });
  const i = lookup.data;
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const d = new FormData(event.currentTarget);
    setError(null);
    if (!i?.account && d.get('password') !== d.get('confirm')) { setError(t('Slaptažodžiai nesutampa.')); return; }
    accept.mutate(i?.account ? { password: d.get('password') } : { name: d.get('name'), password: d.get('password') });
  };
  return (
    <AuthCard eyebrow="FakturaSmart Pro" title={i ? t('Kvietimas į „{company}“', { company: i.company }) : t('Kvietimas')}
      intro={i ? t('{name} kviečia jus prisijungti prie įmonės paskyros. Teisės: {role}.', { name: i.inviter ?? '—', role: t(ROLE_LABELS[i.role]) }) : undefined}
      footer={<Link to="/login">{t('Prisijungti')}</Link>}>
      {lookup.isError ? <Alert kind="error">{errorText(t, lookup.error)}</Alert> : null}
      {i && i.state !== 'open' ? <Alert kind="error">{t(STATE_TEXT[i.state])}</Alert> : null}
      {error ? <Alert kind="error">{error}</Alert> : null}
      {oauthMessage(t, oauth) ? <Alert kind="error">{oauthMessage(t, oauth)}</Alert> : null}
      {accept.isError ? <Alert kind="error">{errorText(t, accept.error)}</Alert> : null}
      {i?.state === 'open' ? <SignInWith invite={token} /> : null}
      {i?.state === 'open' ? (
        <form onSubmit={submit}>
          <label>{t('El. paštas')}<input type="email" name="username" value={i.email} autoComplete="username" readOnly /></label>
          {i.account ? <>
            <p className="muted">{t('Šiuo el. paštu jau turite paskyrą. Patvirtinkite savo slaptažodžiu.')}</p>
            <label>{t('Slaptažodis')}<input type="password" name="password" autoComplete="current-password" required autoFocus /></label>
          </> : <>
            <label>{t('Vardas ir pavardė')}<input name="name" autoComplete="name" required maxLength={120} autoFocus /></label>
            <label>{t('Slaptažodis')}<input type="password" name="password" minLength={8} autoComplete="new-password" required /></label>
            <label>{t('Pakartokite slaptažodį')}<input type="password" name="confirm" minLength={8} autoComplete="new-password" required /></label>
          </>}
          <button className="button button--primary" disabled={accept.isPending}>{t('Priimti kvietimą')}</button>
        </form>
      ) : null}
    </AuthCard>
  );
}
